export type GlobalRoleCode = 'super_admin';

export type AuthUser = {
  id: string;
  email: string | null;
  is_demo: boolean;
  is_active: boolean;
  global_roles: GlobalRoleCode[];
  permissions: string[];
  accessible_profile_ids: string[] | null;
  /** Null only when this account still has to accept Terms. */
  terms_accepted_at?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  /** Null when the company profile intro is still required. */
  profile_completed_at?: string | null;
};

export function isSuperAdmin(user: AuthUser | null | undefined): boolean {
  return Boolean(user?.global_roles?.includes('super_admin'));
}

export function hasPermission(user: AuthUser | null | undefined, code: string): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  return user.permissions.includes(code);
}

export function canAccessProfile(user: AuthUser | null | undefined, profileId: string): boolean {
  if (!user) return false;
  const pid = profileId.trim();
  if (!pid) return false;
  // Demo users are limited to granted demo profiles only (never super-admin bypass).
  if (user.is_demo) {
    return user.accessible_profile_ids?.includes(pid) ?? false;
  }
  if (isSuperAdmin(user)) return true;
  return user.accessible_profile_ids?.includes(pid) ?? false;
}

export function normalizeAuthUser(raw: Record<string, unknown>): AuthUser {
  const globalRoles = Array.isArray(raw.global_roles)
    ? raw.global_roles.filter((r): r is GlobalRoleCode => r === 'super_admin')
    : [];
  const permissions = Array.isArray(raw.permissions)
    ? raw.permissions.filter((p): p is string => typeof p === 'string')
    : [];
  const accessible = raw.accessible_profile_ids;
  return {
    id: String(raw.id ?? ''),
    email: typeof raw.email === 'string' ? raw.email : null,
    is_demo: Boolean(raw.is_demo),
    is_active: raw.is_active !== false,
    global_roles: globalRoles,
    permissions,
    accessible_profile_ids:
      accessible === null || accessible === undefined
        ? null
        : Array.isArray(accessible)
          ? accessible.filter((p): p is string => typeof p === 'string')
          : [],
    ...('terms_accepted_at' in raw
      ? {
          terms_accepted_at:
            typeof raw.terms_accepted_at === 'string' && raw.terms_accepted_at
              ? raw.terms_accepted_at
              : null,
        }
      : {}),
    ...('profile_completed_at' in raw
      ? {
          profile_completed_at:
            typeof raw.profile_completed_at === 'string' && raw.profile_completed_at
              ? raw.profile_completed_at
              : null,
        }
      : {}),
    first_name: typeof raw.first_name === 'string' ? raw.first_name : null,
    last_name: typeof raw.last_name === 'string' ? raw.last_name : null,
    company_name: typeof raw.company_name === 'string' ? raw.company_name : null,
  };
}
